import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { FilterChip } from '../ui/FilterChipRow';

type Filter = { value: string; label: string };

/** Keep the search field and the active filter within reach as the response pane narrows. */
export function ResponseRecipeToolbar({ options, selected, onSelect, search, onSearch }: {
  options: Filter[]; selected: string | null; onSelect: (value: string | null) => void;
  search: string; onSearch: (value: string) => void;
}) {
  const row = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState(3);
  useLayoutEffect(() => {
    const element = row.current;
    if (!element) return undefined;
    const measure = () => {
      const width = element.clientWidth;
      if (width) setSlots(width < 440 ? 1 : width < 640 ? 2 : 3);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    return () => observer?.disconnect();
  }, []);

  const inline = options.slice(0, slots);
  const active = options.find(option => option.value === selected);
  if (active && !inline.includes(active)) inline[inline.length - 1] = active;
  const overflow = options.filter(option => !inline.includes(option));

  return <div ref={row} role="toolbar" aria-label="Recipe tools" className="flex min-w-0 items-center gap-2">
    {options.length > 0 && <div role="group" aria-label="Recipe filters" className="flex min-w-0 items-center gap-1.5">
      <FilterChip label="All" active={!selected} onClick={() => onSelect(null)} className="shrink-0" />
      {/* Keep the button in its slot when a selected overflow tag takes its place. */}
      {inline.map((option, slot) => <FilterChip key={slot} label={option.label} active={selected === option.value}
        onClick={() => onSelect(option.value)} className="min-w-0 max-w-36 truncate">
        <AnimatedLabel text={option.label} truncate />
      </FilterChip>)}
      {overflow.length > 0 && <OverflowFilters options={overflow} onSelect={onSelect} />}
    </div>}
    <label className="relative w-[clamp(8.5rem,36%,14rem)] max-w-full min-w-0 shrink-0">
      <IconSearch aria-hidden size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input type="search" aria-label="Search recipes" placeholder="Search" value={search}
        onChange={event => onSearch(event.target.value)}
        className="h-8 w-full rounded-lg bg-muted pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring" />
    </label>
  </div>;
}

/** Search only the filters that are not already on the row. */
function OverflowFilters({ options, onSelect }: { options: Filter[]; onSelect: (value: string) => void }) {
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();
  const matches = (option: Filter) => option.label.toLowerCase().includes(term);
  const first = options.find(matches);
  const list = useRef<HTMLDivElement>(null);
  return <Popover className="shrink-0">
    {({ close }) => {
      const pick = (option: Filter) => { onSelect(option.value); close(); };
      return <>
        <PopoverButton aria-label="More filters" title="More filters" onClick={() => setQuery('')}
          className="grid size-8 cursor-pointer place-items-center rounded-lg bg-muted text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring motion-reduce:transition-none">
          <IconPlus aria-hidden size={16} />
        </PopoverButton>
        <PopoverPanel focus transition role="dialog" aria-label="More filters" anchor={{ to: 'bottom start', gap: 6, padding: 8 }}
          className="z-50 flex w-56 max-w-[calc(100vw-1rem)] origin-top flex-col rounded-xl border border-border bg-surface-raised p-1.5 shadow-lg transition duration-150 data-closed:-translate-y-1 data-closed:opacity-0 motion-reduce:transition-none">
          <label className="relative block">
            <IconSearch aria-hidden size={16} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="search" aria-label="Find a filter" placeholder="Find a filter" value={query} onChange={event => setQuery(event.target.value)}
              onKeyDown={event => {
                if (event.nativeEvent.isComposing || !first) return;
                if (event.key === 'Enter') { event.preventDefault(); pick(first); }
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  list.current?.querySelector<HTMLButtonElement>('[data-open="true"] button')?.focus();
                }
              }}
              className="h-9 w-full rounded-md bg-muted pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring" />
          </label>
          <div ref={list} className="mt-1 max-h-52 min-h-0 overflow-y-auto overscroll-contain">
            {options.map(option => <AnimatedPresence key={option.value} show={matches(option)}>
              <button type="button" onClick={() => pick(option)}
                className="block min-h-9 w-full cursor-pointer rounded-md px-2 py-1.5 text-left text-sm break-words text-foreground transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none motion-reduce:transition-none">
                {option.label}
              </button>
            </AnimatedPresence>)}
            <AnimatedPresence show={!first}><p role="status" className="px-2 py-2 text-sm text-muted-foreground">No matching filters.</p></AnimatedPresence>
          </div>
        </PopoverPanel>
      </>;
    }}
  </Popover>;
}
